import type { IApplicantQuestion } from '@host/components/NewUI/Components/Utilities/QuestionFieldGenerator';
import type { IAnswer } from '@host/components/NewUI/ApprovalForms/types';

import { PortalRequestError } from '../api/portalRequestError';
import { IPortalState } from './types';

const jsonHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json'
};

const base = (token: string) => `/onboard/portal/${encodeURIComponent(token)}`;

function errorMessage(body: string, fallback: string): string {
  if (!body) return fallback;

  try {
    const parsed = JSON.parse(body) as { error?: unknown; errors?: unknown };
    if (typeof parsed.error === 'string' && parsed.error) return parsed.error;
    if (Array.isArray(parsed.errors) && parsed.errors.length > 0) {
      return parsed.errors.map(String).join(', ');
    }
    return fallback;
  } catch {
    return body;
  }
}

async function expect<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.text();
    throw new PortalRequestError(errorMessage(body, `Request failed: ${res.status}`), res.status);
  }
  return res.json() as Promise<T>;
}

export async function fetchState(token: string): Promise<IPortalState> {
  return expect<IPortalState>(
    await fetch(`${base(token)}/state`, {
      credentials: 'omit',
      headers: { Accept: 'application/json' }
    })
  );
}

export async function uploadFiles(token: string, itemId: string, files: File[], notes?: string) {
  const form = new FormData();
  files.forEach((file) => form.append('files[]', file));
  if (notes != null) form.append('notes', notes);

  return expect(
    await fetch(`${base(token)}/items/${encodeURIComponent(itemId)}/upload`, {
      method: 'POST',
      credentials: 'omit',
      body: form
    })
  );
}

export async function completeTodo(token: string, itemId: string, notes?: string) {
  return expect(
    await fetch(`${base(token)}/items/${encodeURIComponent(itemId)}/complete`, {
      method: 'POST',
      credentials: 'omit',
      headers: jsonHeaders,
      body: JSON.stringify({ notes: notes ?? '' })
    })
  );
}

export async function fetchFormQuestions(
  token: string,
  itemId: string
): Promise<{ form_id: number; fields: IApplicantQuestion[]; answers: IAnswer[] }> {
  return expect<{ form_id: number; fields: IApplicantQuestion[]; answers: IAnswer[] }>(
    await fetch(`${base(token)}/items/${encodeURIComponent(itemId)}/form`, {
      credentials: 'omit',
      headers: { Accept: 'application/json' }
    })
  );
}

export async function submitForm(
  token: string,
  itemId: string,
  payload: { form_id: number; answers_attributes: Record<number, unknown> }
) {
  return expect(
    await fetch(`${base(token)}/items/${encodeURIComponent(itemId)}/submit_form`, {
      method: 'POST',
      credentials: 'omit',
      headers: jsonHeaders,
      body: JSON.stringify(payload)
    })
  );
}

export async function signOffer(token: string, signatureData: string): Promise<IPortalState> {
  return expect<IPortalState>(
    await fetch(`${base(token)}/sign`, {
      method: 'POST',
      credentials: 'omit',
      headers: jsonHeaders,
      body: JSON.stringify({ signature_data: signatureData })
    })
  );
}

export async function declineOffer(token: string, notes: string) {
  return expect(
    await fetch(`${base(token)}/decline`, {
      method: 'POST',
      credentials: 'omit',
      headers: jsonHeaders,
      body: JSON.stringify({ notes })
    })
  );
}

export async function requestResend(token: string) {
  return expect(
    await fetch(`${base(token)}/resend`, {
      method: 'POST',
      credentials: 'omit',
      headers: jsonHeaders
    })
  );
}

export const contractUrl = (token: string) => `${base(token)}/contract`;

export const contractDownloadUrl = (token: string) => `${base(token)}/contract/download`;

export const libraryDocumentUrl = (token: string, itemId: string) =>
  `${base(token)}/items/${encodeURIComponent(itemId)}/library_document`;

export const formDownloadUrl = (token: string, itemId: string) =>
  `${base(token)}/items/${encodeURIComponent(itemId)}/form_download`;

export const additionalDocumentUrl = (token: string, itemId: string) =>
  `${base(token)}/items/${encodeURIComponent(itemId)}/additional_document`;

export const submittedAssetUrl = (token: string, assetId: number) =>
  `${base(token)}/assets/${assetId}`;

// ---- Contract fetch (used by OfferPreview via useQuery) ----

export interface ContractData {
  status: 'preparing' | 'ready';
  pdfBlob: Blob | null;
  html: string | null;
}

export async function fetchContract(token: string): Promise<ContractData> {
  const res = await fetch(contractUrl(token), { credentials: 'omit' });

  if (res.status === 202) {
    return { status: 'preparing', pdfBlob: null, html: null };
  }

  if (!res.ok) {
    const body = await res.text();
    throw new PortalRequestError(body || `Failed (${res.status})`, res.status);
  }

  const contentType = res.headers.get('Content-Type') ?? '';
  if (contentType.includes('application/pdf')) {
    const blob = await res.blob();
    return { status: 'ready', pdfBlob: blob, html: null };
  }

  const text = await res.text();
  return { status: 'ready', pdfBlob: null, html: text };
}
